import { BigNumber } from 'bignumber.js';
import { Trans, useLingui } from '@lingui/react';
import { isNil, toNumber } from 'lodash-es';
import { Typography } from '@startuptools/ui/Typography';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { nameCapitalize } from '../../../pipes/name-capitalize.pipe';
import {
  GqlTransactionKind,
  type GqlShareTransactionGroup,
  type GqlSnrShareCapitalChange,
} from '../../../graphql/base-types.graphql';

export const StgLabel = ({
  stg,
  snrScc,
}: {
  stg: Pick<GqlShareTransactionGroup, 'kind' | 'splitFactor' | 'transactionDate' | 'shareIssueId' | 'warrantProgramId'>;
  snrScc: Pick<GqlSnrShareCapitalChange, 'name' | 'filingNumber' | 'filingYear' | 'serialNumber'> | undefined | null;
}) => {
  const { i18n } = useLingui();
  let label;
  if (stg.kind === GqlTransactionKind.Split) {
    label = (
      <>
        Split -{' '}
        <NumberFormat variant="number" locale={i18n.locale} minimumFractionDigits={0} maximumFractionDigits={20}>
          {stg.splitFactor}
        </NumberFormat>
        :1
      </>
    );
  } else if (stg.kind === GqlTransactionKind.Merge) {
    const fraction = new BigNumber(stg.splitFactor ?? '0').toFraction();
    label = (
      <>
        <Trans id="Merge" /> -{' '}
        <NumberFormat minimumFractionDigits={0} variant="number" locale={i18n.locale}>
          {fraction[0]}
        </NumberFormat>
        :
        <NumberFormat minimumFractionDigits={0} variant="number" locale={i18n.locale}>
          {fraction[1]}
        </NumberFormat>
      </>
    );
  } else if (stg.kind === GqlTransactionKind.Transfer) {
    label = (
      <>
        <Trans id="Transfer" /> - <DateFormat locale={i18n.locale}>{stg.transactionDate}</DateFormat>
      </>
    );
  } else if (stg.kind === GqlTransactionKind.ShareClassConvert) {
    label = (
      <>
        <Trans id="Share Class Convert" /> - <DateFormat locale={i18n.locale}>{stg.transactionDate}</DateFormat>
      </>
    );
  } else if (stg.kind === GqlTransactionKind.BlockPartition) {
    label = (
      <>
        <Trans id="Notification of Transfer" /> - <DateFormat locale={i18n.locale}>{stg.transactionDate}</DateFormat>
      </>
    );
  } else if (!isNil(snrScc)) {
    label = (
      <>
        {snrScc.name ? nameCapitalize(snrScc.name) : ''} - {toNumber(snrScc.filingNumber)}/{toNumber(snrScc.filingYear)}
        -{toNumber(snrScc.serialNumber)}
      </>
    );
  } else if (stg.shareIssueId) {
    label = <Trans id="Share Issue - Unregistered" />;
  } else if (!isNil(stg.warrantProgramId)) {
    label = <Trans id="Subscription Option - Unregistered" />;
  } else if (isNil(label)) {
    label = <Trans id="Unregistered Shares" />;
  }

  return <Typography>{label}</Typography>;
};
